import { useState, useEffect } from "react";
import { Split } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { getMailChannels, type MailChannelsResponse } from "../../../../lib/mail";
import { Card } from "../../../ui";
import { PanelLoader } from "../panel/IntegrationCard";
import { MailChannelPanel } from "./MailChannelPanel";
import { SystemSignatureCard } from "./SystemSignatureCard";

/**
 * Settings -> Integrations: the sending channels.
 *
 * Separate from the mailbox card because they answer different questions. The
 * mailbox card is "which inbox do I write from and read into". This one is
 * "whose account carries which mail", and its whole purpose is that a problem on
 * one identity cannot take down the others.
 *
 * Each channel is its own card rather than a bordered box inside one, for the
 * same reason: they fail independently, so they are read independently. The card
 * above them carries the one sentence that is true of both.
 */
export function MailChannelsCard() {
  const t = useT();
  const [data, setData] = useState<MailChannelsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMailChannels().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <PanelLoader />;
  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* `crm-card` is a surface with no padding of its own, so a card holding
          nothing but prose brings its own gutter. */}
      <Card className="px-[26px] py-5">
        <div className="crm-card-t flex items-center gap-2.5">
          <Split className="size-[17px] shrink-0" style={{ color: "var(--color-primary)" }} />
          {t("settings.channels.title")}
        </div>
        <div className="crm-card-s">{t("settings.channels.desc")}</div>
      </Card>

      {/* Before the channels: it is one field, and the one an operator comes
          here to fill in. It loads by itself, so a slow answer leaves the
          channels below usable. */}
      <SystemSignatureCard />

      <MailChannelPanel
        id="system"
        channel={data.channels.system}
        allowedModes={data.allowedModes.system}
        onSaved={setData}
      />
      <MailChannelPanel
        id="marketing"
        channel={data.channels.marketing}
        allowedModes={data.allowedModes.marketing}
        onSaved={setData}
      />
    </div>
  );
}
